import React, { FC, useEffect, useState, useCallback, useRef } from "react";

import { AxiosError } from "axios";
import { Button, Table } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport } from "@fortawesome/free-solid-svg-icons";
import useSpinnerLoader from "../../../../shared/components/spinner-loader";
import TableHeaderOrdering, {
  Header,
  OrderData,
} from "../../../../shared/components/table-header-ordering";
import { notificationService } from "../../../../shared/services/notification-service";
import TableRowNoData from "../../../../shared/components/molecules/commons/table-row-no-data";
import { HrOnlineService } from "../../../../shared/services/Report/HrOnline/hr-online";
import {
  Document,
  Page,
  View,
  Text,
  PDFDownloadLink,
  Font,
} from "@react-pdf/renderer";

import moment from "moment";

Font.register({
  family: "kanit-regular",
  src: "https://fonts.gstatic.com/s/kanit/v1/L6VKvM17ZmevDynOiw7H9w.ttf",
});
Font.register({
  family: "kanit-200",
  src: "https://fonts.gstatic.com/s/kanit/v1/wfLWkj1C4tYl7MoiFWS3bA.ttf",
});

const thaiMonths = [
  "มกราคม",
  "กุมภาพันธ์",
  "มีนาคม",
  "เมษายน",
  "พฤษภาคม",
  "มิถุนายน",
  "กรกฎาคม",
  "สิงหาคม",
  "กันยายน",
  "ตุลาคม",
  "พฤศจิกายน",
  "ธันวาคม",
];

const ReportEmployeeCountByTypeListPage: FC = () => {
  const [queryParams, setQueryParams] = useState<any>({
    ordering_field: "",
    ordering: "",
    page: 1,
    page_size: 10,
  });
  const [data, setData] = useState<any>([]);
  const [dataExport, setDataExport] = useState<any>([]);
  const { loaderComponent, startLoading, stopLoading } = useSpinnerLoader();
  const loadingRef = useRef({ startLoading, stopLoading });
  loadingRef.current = { startLoading, stopLoading };

  const headers: Header[] = [
    {
      label: "ระดับ",
      field: "level",
      order: null,
      isOrder: true,
    },
    {
      label: "ชาย",
      field: "male",
      order: null,
      isOrder: true,
    },
    {
      label: "หญิง",
      field: "female",
      order: null,
      isOrder: true,
    },
    {
      label: "รวม",
      field: "total",
      order: null,
      isOrder: true,
    },
  ];

  const fetchData = useCallback(
    async (params: any) => {
      loadingRef.current.startLoading();
      try {
        const result = await HrOnlineService.getReportEmployeeCountByTypeList(
          params
        );
        setData(result);
        setDataExport(result);
      } catch (error) {
        const err = error as AxiosError;
        notificationService.dangerNotification(
          "โหลดข้อมูลไม่สำเร็จ",
          err.message
        );
      } finally {
        loadingRef.current.stopLoading();
      }
    },
    [queryParams.ordering_field, queryParams.ordering]
  );

  const tableRowNoData = () => {
    if (data.rows?.personnel_employees.length === 0) {
      return <TableRowNoData colspan={7} />;
    }
    return null;
  };

  const PDF: React.FC<{ data: any }> = ({ data }) => (
    <Document>
      <Page size="A4" style={{ padding: 10 }}>
        <View>
          <Text
            style={{
              fontFamily: "kanit-regular",
              fontSize: 13,
              textAlign: "center",
              marginBottom: 3,
            }}
          >
            องค์การตลาดเพื่อเกษตรกร
          </Text>

          <Text
            style={{
              fontFamily: "kanit-regular",
              fontSize: 13,
              textAlign: "center",
              width: "100%",
              marginBottom: 3,
            }}
          >
            รายงานจำนวนพนักงานและลูกจ้างง
          </Text>

          <Text
            style={{
              fontFamily: "kanit-regular",
              fontSize: 13,
              textAlign: "center",
              marginBottom: 10,
            }}
          >
            ประจำวันที่{" "}
            {moment().format("D") +
              " " +
              thaiMonths[moment().month()] +
              " " +
              (moment().year() + 543)}
          </Text>

          <View style={{ marginBottom: 10 }} fixed>
            {/* หัวตาราง */}
            <View
              style={{
                flexDirection: "row",
                marginBottom: 5,
                borderBottomWidth: 1,
                paddingBottom: 5,
              }}
            >
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 11,
                  textAlign: "center",
                  marginLeft: 5,
                }}
              ></Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 11,
                  textAlign: "center",
                  marginLeft: 5,
                }}
              ></Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 11,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                ชาย
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 11,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                หญิง
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 11,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                รวม
              </Text>
            </View>

            {/* ข้อมูลพนักงาน */}
            {data.rows?.personnel_employees.map((item: any) => (
              <View
                key={item.level}
                style={{ flexDirection: "row", marginBottom: 2 }}
              >
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "20%",
                    fontSize: 10,
                    textAlign: "left",
                    marginLeft: 5,
                  }}
                >
                  ระดับ
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "20%",
                    fontSize: 10,
                    textAlign: "center",
                    marginLeft: 5,
                  }}
                >
                  {item.level}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "20%",
                    fontSize: 10,
                    textAlign: "center",
                    marginRight: 5,
                  }}
                >
                  {item.male_count}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "20%",
                    fontSize: 10,
                    textAlign: "center",
                    marginRight: 5,
                  }}
                >
                  {item.female_count}
                </Text>
                <Text
                  style={{
                    fontFamily: "kanit-200",
                    width: "20%",
                    fontSize: 10,
                    textAlign: "center",
                    marginRight: 5,
                  }}
                >
                  {item.total_count}
                </Text>
              </View>
            ))}

            {/* รวมพนักงาน */}
            <View
              style={{
                flexDirection: "row",
                marginTop: 5,
                borderTopWidth: 1,
                paddingTop: 5,
              }}
            >
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginLeft: 5,
                }}
              ></Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginLeft: 5,
                }}
              >
                รวม
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.personnel_employees_summary?.male}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.personnel_employees_summary?.female}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.personnel_employees_summary?.total}
              </Text>
            </View>

            {/* ลูกจ้างประจำ */}
            <View
              style={{
                flexDirection: "row",
                marginTop: 5,
                paddingTop: 5,
              }}
            >
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginLeft: 5,
                }}
              ></Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginLeft: 5,
                }}
              >
                ลูกจ้างประจำา
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.permanent_employees_summary?.male}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.permanent_employees_summary?.female}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.permanent_employees_summary?.total}
              </Text>
            </View>

            {/* ลูกจ้างชั่วคราว */}
            <View
              style={{
                flexDirection: "row",
                marginTop: 5,
                paddingTop: 5,
              }}
            >
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginLeft: 5,
                }}
              ></Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginLeft: 5,
                }}
              >
                ลูกจ้างชั่วคราว
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.temporary_employees_summary?.male}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.temporary_employees_summary?.female}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.temporary_employees_summary?.total}
              </Text>
            </View>

            {/* รวมทั้งหมด */}
            <View
              style={{
                flexDirection: "row",
                marginTop: 5,
                paddingTop: 5,
              }}
            >
              <Text
                style={{
                  fontFamily: "kanit-200",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginLeft: 5,
                }}
              ></Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginLeft: 5,
                }}
              >
                รวมทุกระดับ
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.total_summary?.male}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.total_summary?.female}
              </Text>
              <Text
                style={{
                  fontFamily: "kanit-regular",
                  width: "20%",
                  fontSize: 10,
                  textAlign: "center",
                  marginRight: 5,
                }}
              >
                {data.rows?.total_summary?.total}
              </Text>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );

  useEffect(() => {
    fetchData(queryParams);
  }, [fetchData]);

  return (
    <>
      <div className="d-flex justify-content-between py-4">
        <h2 className="text-mof-primary m-0">รายงานจำนวนพนักงานและลูกจ้าง</h2>
      </div>
      {loaderComponent}
      <div className="row">
        <div className="col-sm-12 col-md-12 d-flex justify-content-end">
          <PDFDownloadLink
            document={<PDF data={dataExport} />}
            fileName={`รายงานจำนวนพนักงานและลูกจ้าง_${moment().format(
              "DDMMYYYY"
            )}.pdf`}
          >
            {
              (({ loading }: any) => (
                <Button
                  variant="primary"
                  className="btn-action btn-mof-primary me-2"
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faFileExport} className="me-2" />
                  {loading ? "Loading..." : "Export"}
                </Button>
              )) as any
            }
          </PDFDownloadLink>
        </div>
      </div>

      <div className="overflow-auto mt-3">
        <Table striped bordered hover>
          <thead>
            <TableHeaderOrdering
              headers={headers}
              onChangeOrder={(orderData: OrderData) => {
                setQueryParams({
                  ...queryParams,
                  ordering_field: orderData.field,
                  ordering: orderData.order,
                });
              }}
              isShow={true}
            />
          </thead>
          <tbody>
            {data.rows?.personnel_employees.map((data: any, index: any) => {
              return <ReportEmployeeCountByTypeRow key={index} {...data} />;
            })}

            <tr>
              <td>รวม</td>
              <td>{data.rows?.personnel_employees_summary?.male}</td>
              <td>{data.rows?.personnel_employees_summary?.female}</td>
              <td>{data.rows?.personnel_employees_summary?.total}</td>
            </tr>
            <tr>
              <td>ลูกจ้างประจำ</td>
              <td>{data.rows?.permanent_employees_summary?.male}</td>
              <td>{data.rows?.permanent_employees_summary?.female}</td>
              <td>{data.rows?.permanent_employees_summary?.total}</td>
            </tr>
            <tr>
              <td>ลูกจ้างชั่วคราว</td>
              <td>{data.rows?.temporary_employees_summary?.male}</td>
              <td>{data.rows?.temporary_employees_summary?.female}</td>
              <td>{data.rows?.temporary_employees_summary?.total}</td>
            </tr>
            <tr style={{ fontWeight: "bold" }}>
              <td>รวมทุกระดับ</td>
              <td>{data.rows?.total_summary?.male}</td>
              <td>{data.rows?.total_summary?.female}</td>
              <td>{data.rows?.total_summary?.total}</td>
            </tr>
            {tableRowNoData()}
          </tbody>
        </Table>
      </div>
    </>
  );
};

const ReportEmployeeCountByTypeRow: FC<any> = ({
  level,
  male_count,
  female_count,
  total_count,
}) => {
  return (
    <tr>
      <td>{level}</td>
      <td>{male_count}</td>
      <td>{female_count}</td>
      <td>{total_count}</td>
    </tr>
  );
};

export default ReportEmployeeCountByTypeListPage;
